import type { MoreLinkArg } from "@fullcalendar/core"
import { App, Skeleton, Spin } from "antd"
import React, { Suspense, lazy, useCallback } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { calendarSetProp } from "../redux/calendar.slice.ts"
import {
  createCalendarEvent,
  deleteCalendarEvent,
  fetchEventDetails,
  partialUpdateCalendarEvent,
  updateReminderStatus
} from "../redux/calendar.thunks.ts"
import type {
  CalendarModalData,
  CalendarState
} from "../redux/calendar.types.ts"
import type { CalendarPermissions, Event } from "../types/types.ts"

const CalendarAddForm = lazy(
  () => import("./components/CalendarAddForms/CalendarAddForm.tsx")
)

const CalendarView = lazy(
  () => import("./components/CalendarView/CalendarView.tsx")
)

const CalendarMoreEventsModal = lazy(
  () => import("./components/CalendarMoreEvents/CalendarMoreEventsModal.tsx")
)

interface CalendarPageActionsProps {
  dateFormat: string | null
  timezone: string
  userId: string
  permissions: CalendarPermissions
}

export default function CalendarPageActions({
  dateFormat,
  timezone,
  userId,
  permissions
}: CalendarPageActionsProps) {
  const { modal } = App.useApp()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const moreEventsData = useAppSelector(
    (state) => state.calendar?.moreEventsData
  )
  const addModalData = useAppSelector((state) => state.calendar?.addModalData)
  const selectedEvent = useAppSelector((state) => state.calendar?.selectedEvent)
  const saveFetching = useAppSelector(
    (state) => state.calendar?.saveFetching || false
  )
  const fetching = useAppSelector((state) => state.calendar?.fetching || false)

  const handleSetProp = useCallback(
    (key: keyof CalendarState, value: CalendarState[keyof CalendarState]) => {
      dispatch(calendarSetProp({ key, value }))
    },
    [dispatch]
  )

  const handleSetModalData = useCallback(
    (data: CalendarModalData | null) => {
      handleSetProp("addModalData", data)
    },
    [handleSetProp]
  )

  const handleSetMoreEventsData = useCallback(
    (data?: MoreLinkArg) => {
      if (data) {
        handleSetProp("moreEventsData", {
          date: data.date,
          allSegs: data.allSegs
        })
      } else {
        handleSetProp("moreEventsData", null)
      }
    },
    [handleSetProp]
  )

  const handleSelectEvent = useCallback(
    (id: string | null) => {
      if (id) {
        dispatch(fetchEventDetails(id))
      } else {
        handleSetProp("selectedEvent", id)
      }
    },
    [dispatch, handleSetProp]
  )

  const handleDeleteEvent = useCallback(
    (event: Event) => {
      const id = event?.id
      const type = event?.type?.toLowerCase()

      modal.confirm({
        centered: true,
        title: t("Confirm deleting"),
        content: t(
          `Are you sure you want to delete this ${type}? This action can NOT be undone.`
        ),
        cancelText: t("Cancel"),
        okText: t("Delete"),
        okButtonProps: { danger: true, ghost: true },
        onOk() {
          dispatch(deleteCalendarEvent({ id, type }))
        }
      })
    },
    [dispatch, modal, t]
  )

  const handleDoneEvent = useCallback(
    (e: Event, is_completed: boolean) => {
      dispatch(updateReminderStatus({ id: e?.id, type: e?.type, is_completed }))
    },
    [dispatch]
  )

  const handleCreateEvent = useCallback(
    (data: CalendarModalData) => {
      dispatch(createCalendarEvent(data))
    },
    [dispatch]
  )

  const handleUpdateEvent = useCallback(
    (data: Parameters<typeof partialUpdateCalendarEvent>[0]) => {
      dispatch(partialUpdateCalendarEvent(data))
    },
    [dispatch]
  )

  return (
    <>
      {fetching && <Spin fullscreen rootClassName={"z-[1001]"} spinning />}

      {addModalData && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className="fixed z-10 inset-0 m-auto size-52"
            />
          }
        >
          <CalendarAddForm
            initialValues={addModalData}
            saveFetching={saveFetching}
            onClose={() => handleSetModalData(null)}
            onSubmit={handleCreateEvent}
            timezone={timezone}
          />
        </Suspense>
      )}

      {moreEventsData && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className="fixed z-[1001] inset-0 m-auto size-52"
            />
          }
        >
          <CalendarMoreEventsModal
            dateFormat={dateFormat}
            permissions={permissions}
            data={moreEventsData}
            onClose={() => handleSetMoreEventsData()}
            onSelect={handleSelectEvent}
          />
        </Suspense>
      )}

      {selectedEvent && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className="fixed z-[1001] inset-0 m-auto size-52"
            />
          }
        >
          <CalendarView
            dateFormat={dateFormat}
            permissions={permissions}
            userId={userId}
            saveFetching={saveFetching}
            event={selectedEvent}
            timezone={timezone}
            onClose={() => handleSelectEvent(null)}
            onDelete={handleDeleteEvent}
            onDone={handleDoneEvent}
            onSave={handleUpdateEvent}
          />
        </Suspense>
      )}
    </>
  )
}
